import { Alert, Box, Button, LinearProgress, Typography } from "@mui/material";

interface SectionLoadingProgressProps {
  loadedCount: number;
  // How many files there were when last counted,
  // or null if that isn't known.
  expectedCount: number | null;
  isLoadingMore: boolean;
  loadMoreError: unknown;
  onRetry: () => void;
}

const formatCount = (count: number) => count.toLocaleString("en-GB");

// Shown while a long list is still loading in sections, or when a later
// section failed. Files that have already loaded stay usable either way.
const SectionLoadingProgress = ({
  loadedCount,
  expectedCount,
  isLoadingMore,
  loadMoreError,
  onRetry,
}: SectionLoadingProgressProps) => {
  if (loadMoreError) {
    return (
      <Alert
        severity="warning"
        sx={{ mb: 2 }}
        action={
          <Button color="inherit" size="small" onClick={onRetry}>
            Retry
          </Button>
        }
      >
        Only {formatCount(loadedCount)} files could be loaded. Search, filters
        and export only cover these files.
      </Alert>
    );
  }

  if (!isLoadingMore) return null;

  // There may be more files now than when they were counted, so never
  // show more than 99% until loading has actually finished.
  const percent =
    expectedCount && expectedCount > 0
      ? Math.min(99, (loadedCount / expectedCount) * 100)
      : undefined;

  return (
    <Box sx={{ mb: 2 }} role="status">
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        Loading files… {formatCount(loadedCount)}
        {expectedCount ? ` of about ${formatCount(expectedCount)}` : ""} loaded.
        Search, filters and export cover the files loaded so far.
      </Typography>
      <LinearProgress
        variant={percent === undefined ? "indeterminate" : "determinate"}
        value={percent}
        aria-label="Loading files"
      />
    </Box>
  );
};

export default SectionLoadingProgress;
